import Vue from "vue";
import Component from "vue-class-component";
import { Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation } from "vuex-class";
import { storeModules } from "../../../store";
import { IAudioPlayerState } from "../../../store/audio-player/audio-player-types.store";
import EventBus from "../../../utils/event-bus";

const AudioPlayerGetter = namespace(storeModules.AudioPlayer, Getter);
const AudioPlayerMutation = namespace(storeModules.AudioPlayer, Mutation);

@Component({
    template: require("./template.html"),
})
export default class CustomerAudioPlayer extends Vue {
    @AudioPlayerGetter audioUrl: string;
    @AudioPlayerGetter title: string;
    @AudioPlayerGetter isShow: boolean;
    @AudioPlayerMutation setAudioPlayer: (payload: IAudioPlayerState) => void;
    @AudioPlayerMutation setIsPlaying: (payload: boolean) => void;

    isPlaying: boolean = false;
    currentTime: number = 0;
    duration: number = 0;
    progressValue: number = 0;
    isLoading: boolean = false;
    isDragging: boolean = false;

    private audioListeners: Record<string, EventListener> = {};
    private pendingSeek: { url: string; time: number } | null = null;
    private metadataLoadedForUrl: string | null = null;
    private autoPlayOnNextLoad: boolean = true;

    private onBusPauseAudio = () => this.pauseAudio();
    private onBusPlayAudio = () => this.playAudio();
    private onBusClosePlayer = () => this.closePlayer();
    private onBusProgressChange = (value: number) => this.onProgressChange(value);

    get audioElement(): HTMLAudioElement {
        return (this as any).$refs.audioElement as HTMLAudioElement;
    }

    @Watch('audioUrl')
    onAudioUrlChange(newUrl: string) {
        if (newUrl && this.audioElement) {
            this.resetPlayer();
            this.setIsPlaying(false);
            this.metadataLoadedForUrl = null;
            this.autoPlayOnNextLoad = true;
            this.audioElement.load();
        }
    }

    togglePlayPause(): void {
        if (this.isPlaying) {
            this.pauseAudio();
        } else {
            this.playAudio();
        }
    }

    async playAudio(): Promise<void> {
        try {
            if (this.audioElement) {
                await this.audioElement.play();
                this.setIsPlaying(true);
            }
        } catch (error) {
            if (this.$message) {
                this.$message.error('Cannot play audio');
            }
        }
    }

    pauseAudio(): void {
        if (this.audioElement) {
            this.audioElement.pause();
            this.setIsPlaying(false);
        }
    }

    onPlay(): void {
        this.isPlaying = true;
        this.setIsPlaying(true);
    }

    onPause(): void {
        this.isPlaying = false;
        this.setIsPlaying(false);
    }

    onProgressChange(value: number): void {
        this.isDragging = false;
        if (!this.audioElement) return;
        if (this.metadataLoadedForUrl === this.audioUrl && this.duration > 0) {
            this.audioElement.currentTime = value;
        } else {
            this.pendingSeek = { url: this.audioUrl, time: value };
        }
    }

    onProgressInput(): void {
        this.isDragging = true;
    }

    onLoadedMetadata(): void {
        if (this.audioElement) {
            this.duration = this.audioElement.duration || 0;
            this.progressValue = 0;
            this.currentTime = 0;
            this.metadataLoadedForUrl = this.audioUrl;
            this.applyPendingSeek();

            this.$nextTick(() => {
                this.$forceUpdate();
            });
        }
    }

    private applyPendingSeek(): void {
        if (!this.pendingSeek || !this.audioElement || this.duration <= 0) return;
        if (this.pendingSeek.url !== this.audioUrl) return;
        this.audioElement.currentTime = this.pendingSeek.time;
        this.currentTime = this.pendingSeek.time;
        this.progressValue = this.pendingSeek.time;
        this.pendingSeek = null;
    }

    onTimeUpdate(): void {
        if (this.isDragging) return;
        if (this.audioElement) {
            const newTime = this.audioElement.currentTime;
            if (Math.abs(newTime - this.currentTime) > 0.1) {
                this.currentTime = newTime;
                this.progressValue = newTime;
            }
        }
    }

    onEnded(): void {
        this.isPlaying = false;
        this.setIsPlaying(false);
        this.currentTime = 0;
        this.progressValue = 0;
        this.$emit('ended');
    }

    onError(error: Event): void {
        if (!this.audioUrl) return;
        this.isPlaying = false;
        this.setIsPlaying(false);
        if (this.$message) {
            this.$message.error('Error loading audio');
        }
        this.$emit('error', error);
    }

    formatTime(time: number): string {
        if (isNaN(time) || time < 0 || !isFinite(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    closePlayer(): void {
        this.pauseAudio();
        this.setAudioPlayer({
            audioUrl: null,
            title: null,
            isShow: false,
            recordId: null
        })
        this.$emit('close');
    }

    openSettings(): void {
        this.$emit('settings');
    }

    resetPlayer(): void {
        this.isPlaying = false;
        this.currentTime = 0;
        this.duration = 0;
        this.progressValue = 0;
        this.isDragging = false;
    }

    mounted(): void {
        this.$nextTick(() => {
            if (!this.audioElement) return;

            this.audioElement.volume = 0.8;

            this.audioListeners.loadstart = () => { this.isLoading = true; };
            this.audioListeners.canplay = () => {
                this.isLoading = false;
                this.applyPendingSeek();
                if (this.autoPlayOnNextLoad) {
                    this.autoPlayOnNextLoad = false;
                    this.playAudio();
                }
            };
            this.audioListeners.durationchange = () => {
                this.duration = this.audioElement.duration || 0;
                this.applyPendingSeek();
            };
            Object.entries(this.audioListeners).forEach(([event, handler]) => {
                this.audioElement.addEventListener(event, handler);
            });

            if (this.audioUrl) {
                this.audioElement.load();
            }
        });

        EventBus.$on('pauseAudio', this.onBusPauseAudio);
        EventBus.$on('playAudio', this.onBusPlayAudio);
        EventBus.$on('closeAudioPlayer', this.onBusClosePlayer);
        EventBus.$on('onProgressChange', this.onBusProgressChange);
    }

    beforeDestroy(): void {
        if (this.audioElement) {
            this.pauseAudio();
            Object.entries(this.audioListeners).forEach(([event, handler]) => {
                this.audioElement.removeEventListener(event, handler);
            });
        }
        this.audioListeners = {};

        EventBus.$off('pauseAudio', this.onBusPauseAudio);
        EventBus.$off('playAudio', this.onBusPlayAudio);
        EventBus.$off('closeAudioPlayer', this.onBusClosePlayer);
        EventBus.$off('onProgressChange', this.onBusProgressChange);
    }
}